import { useEffect, useMemo } from "react";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import type { CbuCvuSendRecipient } from "../../utils/walletSend";
import {
  CBU_CVU_TYPES,
  cbuCvuRecipientSchema,
  type CbuCvuRecipientValues,
  EMPTY_CBU_CVU_VALUES,
} from "./sendRecipients.schema";

export interface CbuCvuRecipientFormProps {
  defaults: CbuCvuRecipientValues;
  /** Every edit: the raw values, and the recipient once they are complete. */
  onChange: (values: CbuCvuRecipientValues, recipient: CbuCvuSendRecipient | null) => void;
}

const TYPE_LABEL = { CVU: "step2_account_type_cvu", CBU: "step2_account_type_cbu" } as const;

const digitsUpTo = (max: number) => (raw: string) => raw.replace(/\D/g, "").slice(0, max);

/** A CBU (bank) or CVU (wallet) and its holder (`CbuCvuDestinationDto`); the CUIT/CUIL is optional. */
export function CbuCvuRecipientForm({ defaults, onChange }: CbuCvuRecipientFormProps) {
  const { t } = useTranslations("dispersions");
  const schema = useMemo(
    () =>
      cbuCvuRecipientSchema({
        accountNumber: t("error_cbu_cvu_invalid"),
        cuit: t("error_cuit_invalid"),
        name: t("error_recipient_name_required"),
      }),
    [t],
  );
  const form = useZodForm(schema, defaults);
  const accountType = form.watch("accountType");

  useEffect(() => {
    const report = (values: Partial<CbuCvuRecipientValues>) => {
      const full = { ...EMPTY_CBU_CVU_VALUES, ...values } as CbuCvuRecipientValues;
      const parsed = schema.safeParse(full);
      onChange(
        full,
        parsed.success
          ? {
              rail: "CBU_CVU",
              accountType: parsed.data.accountType,
              accountNumber: parsed.data.accountNumber,
              cuitCuil: parsed.data.cuitCuil || null,
              beneficiaryName: parsed.data.beneficiaryName,
            }
          : null,
      );
    };
    report(form.getValues());
    const subscription = form.watch((values) => report(values as Partial<CbuCvuRecipientValues>));
    return () => subscription.unsubscribe();
  }, [form, schema, onChange]);

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="space-y-4">
      <ColocaInputField
        name="beneficiaryName"
        label={t("send_name_label")}
        placeholder={t("send_name_placeholder")}
        maxLength={120}
        autoComplete="off"
        required
      />
      <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)]">
        <ColocaSelectField
          name="accountType"
          label={t("step3_account_type_label")}
          items={CBU_CVU_TYPES.map((type) => ({ value: type, label: t(TYPE_LABEL[type]) }))}
          required
        />
        <ColocaInputField
          name="accountNumber"
          label={accountType}
          placeholder={t("step2_cbu_cvu_placeholder")}
          description={t("step2_cbu_cvu_helper")}
          inputMode="numeric"
          maxLength={22}
          autoComplete="off"
          transform={digitsUpTo(22)}
          required
        />
      </div>
      <ColocaInputField
        name="cuitCuil"
        label={t("step2_cuit_label")}
        placeholder={t("step2_cuit_placeholder")}
        description={t("step2_cuit_helper")}
        inputMode="numeric"
        maxLength={11}
        autoComplete="off"
        transform={digitsUpTo(11)}
      />
    </ColocaForm>
  );
}
